import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// API Response types
export interface ApiResponse {
  // Generic response structure
  [key: string]: any;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  role: string;
  createdAt: string;
}

// Admin user shape returned by /admin/auth/login
export interface AdminUser {
  id: string;
  email: string;
  role: string;
  firstName: string;
  lastName: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
}

// Shape returned by POST /admin/auth/login
export interface AdminAuthResponse {
  access_token: string;
  user: AdminUser;
}

// Shape returned by GET /admin/auth/me
export interface AdminProfile {
  id: string;
  email: string;
  role: string;
  status: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isActive: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

// Shape returned by GET /admin/users (array item)
export interface AdminUserRecord {
  id: string;
  email: string;
  role: string;
  status: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminUserRequest {
  email: string;
  password?: string;
  role: string;
  companyId: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
}

export interface UpdateAdminUserRequest {
  email?: string;
  password?: string;
  role?: string;
  companyId?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface AdminCardRecord {
  id: string;
  cardReference: string;
  maskedCardNumber: string;
  status: string;
  metadata: any;
  printDetails: any;
  activatedAt: string | null;
  vehicleId: string | null;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

// Shape returned by GET /admin/vehicles
export interface AdminVehicleRecord {
  id: string;
  plateNumber?: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  imei?: string;
  contractId?: string;
  department?: string;
  assignedDriver?: string;
  tankCapacity?: number;
  baseOdometer?: number;
  fuelBaseline?: number;
  useVirtualFuelTracking?: boolean;
  iccid?: string;
  trackerModel?: string;
  companyId: string;
  /** Direct card fields — populated from cards[0] on the backend */
  cardId?: string;
  cardReference?: string;
  status?: string;
  /** The full cards array as returned by the 'cards' relation */
  cards?: Array<{
    id: string;
    cardReference: string;
    maskedCardNumber?: string;
    status: string;
    vehicleId?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  company?: {
    id: string;
    legalName: string;
  };
}

export interface CreateAdminVehicleRequest {
  companyId: string;
  cardId: string;
  plateNumber?: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  imei?: string;
  contractId?: string;
  department?: string;
  assignedDriver?: string;
  tankCapacity?: number;
  baseOdometer?: number;
  fuelBaseline?: number;
  useVirtualFuelTracking?: boolean;
  iccid?: string;
  trackerModel?: string;
}


export interface AdminCompanyRecord {
  id: string;
  legalName: string;
  contactEmail: string;
  taxId?: string;
  address?: string;
  contactPhone?: string;
  preferences?: any;
  logoUrl?: string;
  createdAt: string;
  updatedAt: string;
  cardCount?: number;
  cardBalance?: number;
  walletBalance?: number;
  balance?: number;
  monthlySpend?: number;
  totalSpend?: number;
}

export interface CreateAdminCompanyRequest {
  legalName: string;
  contactEmail: string;
  password?: string;
}

export interface AdminDashboardSummary {
  totalCompanies: number;
  activeCompanies: number;
  totalCardsIssued: number;
  activeCards: number;
  suspendedCards: number;
  monthlyRevenue: number;
  totalRunningBalance: number;
  totalTransactions: number;
}

export interface AnalyticsRealTimeActivity {
  title: string;
  description: string;
  time: string;
  type: 'success' | 'warning' | 'error' | 'info';
}

export interface AnalyticsTopCompany {
  name: string;
  spending: number;
}

export interface AnalyticsFuelType {
  fuelType: string;
  percentage: number;
}

export interface AnalyticsSuccessRate {
  label: string;
  value: number;
}

export interface AnalyticsKPIs {
  companiesStatistics: number;
  userIssuanceRecords: number;
  flaggedTransactions: number;
  cardRequests: number;
}

export interface AnalyticsKeyLocation {
  city: string;
  count: number;
}

export interface AnalyticsActionableInsight {
  type: 'warning' | 'error' | 'info' | 'success';
  text: string;
}

export interface AdminDashboardAnalytics {
  realTimeActivity: AnalyticsRealTimeActivity[];
  topCompaniesBySpending: AnalyticsTopCompany[];
  fuelTypeDistribution: AnalyticsFuelType[];
  transactionSuccessRate: AnalyticsSuccessRate[];
  kpis: AnalyticsKPIs;
  keyLocations: AnalyticsKeyLocation[];
  recentAlerts: AnalyticsRealTimeActivity[];
  actionableInsights: AnalyticsActionableInsight[];
}

export interface UpdateAdminCompanyRequest {
  legalName?: string;
  contactEmail?: string;
  isActive?: boolean;
}

export interface AdminTransaction {
  id: string;
  userId: string;
  amount: number;
  currency: string;
  type: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  reference: string;
  description: string;
  metadata: any;
  vehicleId: string | null;
  createdAt: string;
  onafriqAccountId?: string;
  cid?: string;
  onafriqTransactionId?: string;
  transactionDate?: string;
  transactionTime?: string;
  runningBalance?: number | string;
  fee?: number | string;
  totalAmount?: number | string;
  merchantName?: string;
  vehicle?: {
    id: string;
    plateNumber: string;
    make?: string;
    model?: string;
  };
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    company?: {
      id: string;
      legalName: string;
    };
  };
}

export interface AdminTransactionQuery {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || "https://api.energysmartng.com/api/v1",
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '15000'),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle common errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/';
        }

        return Promise.reject(this.handleApiError(error));
      }
    );
  }

  private handleApiError(error: any): ApiError {
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data?.message || 'An error occurred',
        code: error.response.data?.code,
        details: error.response.data,
      };
    } else if (error.request) {
      // Network error
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      };
    } else {
      // Other error
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
      };
    }
  }

  // Generic request method
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.api.request<T>(config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Auth methods
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.request<AuthResponse>({
      method: 'POST',
      url: 'auth/login',
      data: credentials,
    });
  }

  // Admin login — POST /admin/auth/login
  async adminLogin(credentials: LoginCredentials): Promise<AdminAuthResponse> {
    return this.request<AdminAuthResponse>({
      method: 'POST',
      url: 'admin/auth/login',
      data: credentials,
    });
  }

  async logout(): Promise<void> {
    try {
      await this.request({
        method: 'POST',
        url: 'auth/logout',
      });
    } finally {
      // Clear local storage regardless of API response
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
  }

  async refreshToken(): Promise<{ token: string }> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    return this.request<{ token: string }>({
      method: 'POST',
      url: 'auth/refresh',
      data: { refreshToken },
    });
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>({
      method: 'GET',
      url: 'auth/me',
    });
  }

  // GET /admin/auth/me — fetch current admin profile
  async getAdminProfile(): Promise<AdminProfile> {
    return this.request<AdminProfile>({
      method: 'GET',
      url: 'admin/auth/me',
    });
  }

  // GET /admin/users — fetch all users
  async getAllUsers(): Promise<AdminUserRecord[]> {
    return this.request<AdminUserRecord[]>({
      method: 'GET',
      url: 'admin/users',
    });
  }

  // POST /admin/users — create a new user
  async createAdminUser(userData: CreateAdminUserRequest): Promise<AdminUserRecord> {
    return this.request<AdminUserRecord>({
      method: 'POST',
      url: 'admin/users',
      data: userData,
    });
  }

  // GET /admin/users/pending — fetch all pending users
  async getPendingUsers(): Promise<AdminUserRecord[]> {
    return this.request<AdminUserRecord[]>({
      method: 'GET',
      url: 'admin/users/pending',
    });
  }

  // POST /admin/users/{id}/approve — approve a pending user
  async approveAdminUser(id: string): Promise<AdminUserRecord> {
    return this.request<AdminUserRecord>({
      method: 'POST',
      url: `admin/users/${id}/approve`,
    });
  }

  // POST /admin/users/{id}/reject — reject and block a pending user
  async rejectAdminUser(id: string): Promise<AdminUserRecord> {
    return this.request<AdminUserRecord>({
      method: 'POST',
      url: `admin/users/${id}/reject`,
    });
  }

  // GET /admin/users/{id} — get a specific user by id
  async getAdminUserById(id: string): Promise<AdminUserRecord> {
    return this.request<AdminUserRecord>({
      method: 'GET',
      url: `admin/users/${id}`,
    });
  }

  // PUT /admin/users/{id} — update user details
  async updateAdminUser(id: string, userData: UpdateAdminUserRequest): Promise<AdminUserRecord> {
    return this.request<AdminUserRecord>({
      method: 'PUT',
      url: `admin/users/${id}`,
      data: userData,
    });
  }

  // DELETE /admin/users/{id} — delete a specific user
  async deleteAdminUser(id: string): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `admin/users/${id}`,
    });
  }

  // GET /admin/card/requested — fetch all requested not issued cards
  async getRequestedCards(): Promise<AdminCardRecord[]> {
    return this.request<AdminCardRecord[]>({
      method: 'GET',
      url: 'admin/card/requested',
    });
  }

  // GET /admin/card/{id}/status — check the status of a specific card
  async checkCardStatus(id: string): Promise<any> {
    return this.request<any>({
      method: 'GET',
      url: `admin/card/${id}/status`,
    });
  }

  // POST /admin/card/{id}/issue — issue a previously requested card
  async issueCard(id: string, data?: any): Promise<AdminCardRecord> {
    console.log('issue card', data);
    return this.request<AdminCardRecord>({
      method: 'POST',
      url: `admin/card/${id}/issue`,
      data: data || {},
    });
  }

  // GET /admin/card/ping — ping onafriq service
  async pingOnafriq(): Promise<any> {
    console.log('pinging Onafriq service...');
    return this.request<any>({
      method: 'GET',
      url: 'admin/card/ping',
    });
  }

  // GET /admin/companies — fetch all companies
  async getAdminCompanies(): Promise<AdminCompanyRecord[]> {
    return this.request<AdminCompanyRecord[]>({
      method: 'GET',
      url: 'admin/companies',
    });
  }

  async getDashboardSummary(): Promise<AdminDashboardSummary> {
    return this.request<AdminDashboardSummary>({
      method: 'GET',
      url: 'admin/dashboard/summary',
    });
  }

  // POST /admin/companies — create a new company
  async createAdminCompany(data: CreateAdminCompanyRequest): Promise<AdminCompanyRecord> {
    return this.request<AdminCompanyRecord>({
      method: 'POST',
      url: 'admin/companies',
      data,
    });
  }

  // GET /admin/companies/{id} — get a specific company by id
  async getAdminCompanyById(id: string): Promise<AdminCompanyRecord> {
    return this.request<AdminCompanyRecord>({
      method: 'GET',
      url: `admin/companies/${id}`,
    });
  }

  // PUT /admin/companies/{id} — update company details
  async updateAdminCompany(id: string, data: UpdateAdminCompanyRequest): Promise<AdminCompanyRecord> {
    return this.request<AdminCompanyRecord>({
      method: 'PUT',
      url: `admin/companies/${id}`,
      data,
    });
  }

  // POST /admin/companies/{id}/logo — upload company logo
  async uploadAdminCompanyLogo(id: string, file: File): Promise<AdminCompanyRecord> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.request<AdminCompanyRecord>({
      method: 'POST',
      url: `admin/companies/${id}/logo`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // DELETE /admin/companies/{id} — delete a specific company
  async deleteAdminCompany(id: string): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `admin/companies/${id}`,
    });
  }

  // Fuel card management methods
  async getFuelCards(): Promise<any[]> {
    return this.request<any[]>({
      method: 'GET',
      url: 'admin/card',
    });
  }

  async requestFuelCard(data: { companyId: string; printDetails?: any }): Promise<any> {
    return this.request<any>({
      method: 'POST',
      url: 'admin/card/request',
      data,
    });
  }

  // POST /admin/card/import — import a registered Onafriq card and assign it to a company
  async createCardForClient(data: {
    companyId: string;
    cid: string;
    contractId?: string;
  }): Promise<any> {
    return this.request<any>({
      method: 'POST',
      url: 'admin/card/import',
      data: {
        accountId: data.cid,
        companyId: data.companyId,
        contractId: data.contractId,
      },
    });
  }

  // POST /admin/card/import-csv — bulk import Onafriq card accounts from CSV
  async importCardsCsv(file: File, companyId: string): Promise<{ message: string; rowsDetected: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', companyId);
    return this.request<{ message: string; rowsDetected: number }>({
      method: 'POST',
      url: 'admin/card/import-csv',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async toggleCardStatus(cardReference: string, action: 'LOCK' | 'UNLOCK'): Promise<any> {
    return this.request<any>({
      method: 'PATCH',
      url: `admin/card/${cardReference}/toggle-status`,
      data: { action },
    });
  }

  async loadCardFunds(cardReference: string, amount: number): Promise<any> {
    return this.request<any>({
      method: 'POST',
      url: `admin/card/${cardReference}/load`,
      data: { amount },
    });
  }

  async getCardTransactions(cardReference: string): Promise<any[]> {
    return this.request<any[]>({
      method: 'GET',
      url: `admin/card/${cardReference}/transactions`,
    });
  }
  // PATCH /admin/card/{cardId}/edit — update card record (reassign company, change status, unassign vehicle)
  async updateCard(
    cardId: string,
    updates: { companyId?: string; status?: string; vehicleId?: string | null }
  ): Promise<any> {
    return this.request<any>({
      method: 'PATCH',
      url: `admin/card/${cardId}/edit`,
      data: updates,
    });
  }

  // DELETE /admin/card/{cardId} — permanently delete a fuel card record
  async deleteCard(cardId: string): Promise<{ success: boolean; message: string; id: string }> {
    return this.request<{ success: boolean; message: string; id: string }>({
      method: 'DELETE',
      url: `admin/card/${cardId}`,
    });
  }

  // Billing methods
  async getBillingDashboard(): Promise<any> {
    return this.request<any>({
      method: 'GET',
      url: 'admin/billing/dashboard',
    });
  }

  async getInvoices(query?: any): Promise<PaginatedResponse<any>> {
    return this.request<PaginatedResponse<any>>({
      method: 'GET',
      url: 'admin/billing/invoices',
      params: query,
    });
  }

  async generateInvoices(data: { period: string; startDate: string; endDate: string }): Promise<any[]> {
    return this.request<any[]>({
      method: 'POST',
      url: 'admin/billing/invoices/generate',
      data,
    });
  }

  // Analytics methods
  async getAnalytics(params?: any): Promise<AdminDashboardAnalytics> {
    return this.request<AdminDashboardAnalytics>({
      method: 'GET',
      url: 'admin/dashboard/analytics',
      params,
    });
  }

  // Admin Transactions method
  async getAdminTransactions(query?: AdminTransactionQuery): Promise<PaginatedResponse<AdminTransaction>> {
    return this.request<PaginatedResponse<AdminTransaction>>({
      method: 'GET',
      url: 'admin/transactions',
      params: query,
    });
  }

  // Card Activity Report method
  async getCardActivity(query?: AdminTransactionQuery): Promise<PaginatedResponse<AdminTransaction>> {
    return this.request<PaginatedResponse<AdminTransaction>>({
      method: 'GET',
      url: 'admin/transactions/card-activity',
      params: query,
    });
  }

  // GET /admin/dashboard/fraud-analytics — comprehensive fraud and security analytics data
  async getFraudAnalytics(): Promise<any> {
    return this.request<any>({
      method: 'GET',
      url: 'admin/dashboard/fraud-analytics',
    });
  }

  // POST /admin/transactions/upload-csv — upload previous transactions via CSV
  async uploadTransactionsCsv(file: File): Promise<{ success: number; skipped: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<{ success: number; skipped: number; errors: string[] }>({
      method: 'POST',
      url: 'admin/transactions/upload-csv',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 80000, // Increase timeout to 80s for file uploads
    });
  }

  async generateMonthlyReport(): Promise<Blob> {
    try {
      const response = await this.api.request<Blob>({
        method: 'GET',
        url: 'admin/reports/monthly',
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      // Fallback if backend not implemented
      console.warn("Backend not implemented for monthly report, using fallback blob");
      const fakeCsv = "Month,Revenue,Transactions\nJune,10000,50";
      return new Blob([fakeCsv], { type: 'text/csv' });
    }
  }

  async exportTransactionsCsv(): Promise<Blob> {
    try {
      const response = await this.api.request<Blob>({
        method: 'GET',
        url: 'admin/transactions/export',
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      // Fallback if backend not implemented
      console.warn("Backend not implemented for export, using fallback blob");
      const fakeCsv = "Transaction ID,Amount,Status\nTX123,500,Success";
      return new Blob([fakeCsv], { type: 'text/csv' });
    }
  }

  // ── Vehicle Management ────────────────────────────────────────────────────

  // GET /admin/vehicles?companyId= — list all vehicles (optionally filter by company)
  async getAdminVehicles(companyId?: string): Promise<AdminVehicleRecord[]> {
    return this.request<AdminVehicleRecord[]>({
      method: 'GET',
      url: 'admin/vehicles',
      params: companyId ? { companyId } : undefined,
    });
  }

  // GET /admin/vehicles/:id — single vehicle
  async getAdminVehicle(id: string): Promise<AdminVehicleRecord> {
    return this.request<AdminVehicleRecord>({
      method: 'GET',
      url: `admin/vehicles/${id}`,
    });
  }

  // POST /admin/vehicles — create vehicle for a company
  async createAdminVehicle(data: CreateAdminVehicleRequest): Promise<AdminVehicleRecord> {
    return this.request<AdminVehicleRecord>({
      method: 'POST',
      url: 'admin/vehicles',
      data,
    });
  }

  // PUT /admin/vehicles/:id — update vehicle
  async updateAdminVehicle(id: string, data: Partial<CreateAdminVehicleRequest>): Promise<AdminVehicleRecord> {
    return this.request<AdminVehicleRecord>({
      method: 'PUT',
      url: `admin/vehicles/${id}`,
      data,
    });
  }

  // DELETE /admin/vehicles/:id — delete vehicle
  async deleteAdminVehicle(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>({
      method: 'DELETE',
      url: `admin/vehicles/${id}`,
    });
  }

  // GET /admin/vehicles — fetch cards unassigned to any vehicle for a company (uses existing fuel cards list)
  async getUnassignedCardsForCompany(companyId: string): Promise<AdminCardRecord[]> {
    const allCards = await this.getFuelCards();
    return allCards.filter(
      (c: AdminCardRecord) => c.companyId === companyId && !c.vehicleId && c.status !== 'pending'
    );
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService;
